// src/features/reports/ReportShell.jsx
//
// REPORTS-CANONICAL-HEADER
// Shared report navigation chrome for the standardised portfolio reports.
// The global ShellV2 top bar owns the single report title.
// ReportShell owns only the navigation strip below it.
//
// Props:
//   onBack     — click handler for the "‹ Reports" back link
//   rightSlot  — optional ReactNode rendered at the right end of the back-nav
//                row (e.g. LocationNavPair Prev/Next pills), matching the
//                DetailUnifiedBar rightSlot position used by other reports.
//   prevReport — optional previous report record: { key, title }
//   nextReport — optional next report record: { key, title }
//   onNavigateReport — callback receiving the selected report key
//   children   — report body content
//   recordNavigation — opt approved report details into shared record chrome
//                      and Home's scrolling entry gap. Other readers stay local.

import React from "react";
import { T } from "../../app/tokens.js";
import { DetailUnifiedBar } from "../../components/DetailUnifiedBar.jsx";
import { DetailNavBand } from "../../components/DetailRecordChrome.jsx";
import { LocationNavPair } from "../../v2/locations/LocationNavPair.jsx";
import { useUnifiedReportChrome } from "../../components/OneRowChrome.jsx";

export function ReportShell({
  onBack,
  rightSlot = null,
  prevReport = null,
  nextReport = null,
  onNavigateReport,
  showHeader = true,
  recordNavigation = false,
  backTestId = "report-back-button",
  contentStyle = null,
  children,
}) {
  const unifiedChrome = useUnifiedReportChrome();
  // Report navigation belongs to the shared shell so every report gets the
  // same Offices-style pager, including explicitly legacy-header reports.
  // Keep rightSlot as a compatibility escape hatch for other shell callers.
  const reportPager = (prevReport || nextReport) ? (
    <LocationNavPair
      prevLabel={prevReport?.title ?? null}
      nextLabel={nextReport?.title ?? null}
      onPrev={prevReport ? () => onNavigateReport?.(prevReport.key) : undefined}
      onNext={nextReport ? () => onNavigateReport?.(nextReport.key) : undefined}
    />
  ) : null;
  const resolvedRightSlot = reportPager ?? rightSlot;

  return (
    <div data-testid="report-header" style={{ width: "100%" }}>
      {showHeader && !unifiedChrome && (
        <DetailNavBand
          data-testid="report-navigation-strip"
        >
          <DetailUnifiedBar
            testId="report-unified-bar"
            barContentTestId="report-bar-content"
            backLabel="Reports"
            backAriaLabel="‹ Reports"
            onBack={onBack}
            backTestId={backTestId}
            rightSlot={resolvedRightSlot}
            recordNavigation
          />
        </DetailNavBand>
      )}
      {recordNavigation
        ? <div data-testid="report-content-entry" className="object-detail-entry">
          {contentStyle ? <div style={contentStyle}>{children}</div> : children}
        </div>
        : contentStyle ? <div style={contentStyle}>{children}</div> : children}
    </div>
  );
}

export function ReportAccessNotice({ reportName, onBack, loading = false, recordNavigation = true }) {
  return (
    <ReportShell
      recordNavigation={recordNavigation}
      onBack={onBack}
    >
      <div data-testid="report-access-unavailable" style={{ maxWidth: 1000, margin: "0 auto", padding: "32px", color: T.mid }}>
        {loading ? `Checking access to ${reportName}…` : `${reportName} is unavailable because Offices are disabled.`}
      </div>
    </ReportShell>
  );
}

// Kept as a compatibility export for callers that have not yet migrated to the
// canonical shell contract.
export function ReportHeader(props) {
  return <ReportShell {...props} />;
}

// ─── ReportBandTile ──────────────────────────────────────────────────────────
// KPI-tile-dimensioned band tile for reports that show urgency/status summary
// strips (Lease Countdown, Office Utilisation, Office Capacity Planning).
//
// Matches KPI_TILE_ROW_HEIGHT + KpiTile visual conventions (border-radius 14,
// padding 8/14, flex-column / space-between) while retaining RAG-coloured
// fills and click-to-filter toggle behaviour that BandTile provided.
//
// Props:
//   band    — { key, label, bg, color } from the report's BAND_DEFS array
//   count   — integer count to display prominently
//   active  — boolean; when true renders a high-contrast outline (filter is on)
//   onClick — optional click handler; makes the tile a toggle button

import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";

export function ReportBandTile({ band, count, active, onClick }) {
  const isClickable = typeof onClick === "function";
  return (
    <div
      data-testid={`band-tile-${band.key}`}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-pressed={isClickable ? active : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable
        ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }
        : undefined}
      style={{
        height: KPI_TILE_ROW_HEIGHT,
        boxSizing: "border-box",
        padding: "8px 14px 14px",
        background: band.bg,
        color: band.color,
        borderRadius: 14,
        border: active ? `2px solid ${T.dark}` : `1px solid ${T.border}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: isClickable ? "pointer" : undefined,
        opacity: isClickable && !active && active !== undefined ? 0.7 : 1,
        overflow: "hidden",
        minHeight: 0,
        fontSize: T.fsXs,
        fontWeight: T.fwBold,
        textTransform: "uppercase",
        letterSpacing: ".05em",
      }}
    >
      <div style={{
        fontSize: T.fsXl,
        fontWeight: T.fwDisplay,
        lineHeight: 1.1,
        letterSpacing: 0,
        textTransform: "none",
      }}>
        {count}
      </div>
      {band.label}
    </div>
  );
}
